'use client'

import { useMemo, useState } from 'react'

import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { isError } from 'lodash'
import { SearchIcon } from 'lucide-react'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import { FilterScrollFade } from '~/app/accounting/filters/FilterScrollFade'
import {
  COLUMN_IDS,
  getColumnFilterValue,
  setColumnFilterValue,
} from '~/app/accounting/filters/helpers'
import { parseMultiSelectFilterValue } from '~/components/Table'
import {
  CheckboxGroupInput,
  EmptyState,
  Group,
  NetworkDisplay,
} from '~/components/component-library'
import { Input } from '~/components/inputs/Input'
import { getAllChains, hasCapability } from '~/constants/chains'
import { sortChainIds } from '~/utils'
import { getChainLabel } from '~/utils/chainLabels'

export const NetworkFilterButton = ({
  columnFilters,
  setColumnFilters,
}: {
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}) => {
  const chainIds = useMemo(
    () =>
      sortChainIds(
        getAllChains()
          .filter((chain) => hasCapability(chain.chainId, 'accounting'))
          .map((chain) => chain.chainId),
      ),
    [],
  )

  const rawValue = getColumnFilterValue(columnFilters, COLUMN_IDS.CHAIN_ID)
  const appliedIds = useMemo(() => {
    if (!rawValue) return [] as string[]
    const parsed = parseMultiSelectFilterValue(rawValue)
    return isError(parsed) ? ([] as string[]) : parsed
  }, [rawValue])

  const [localIds, setLocalIds] = useState<string[]>(appliedIds)
  const [search, setSearch] = useState('')
  const [syncedRawValue, setSyncedRawValue] = useState(rawValue)
  if (syncedRawValue !== rawValue) {
    setSyncedRawValue(rawValue)
    setLocalIds(appliedIds)
  }

  const query = search.trim().toLowerCase()
  const visibleChainIds = query
    ? chainIds.filter((chainId) =>
        getChainLabel(chainId).toLowerCase().includes(query),
      )
    : chainIds

  const isLocalEmpty = localIds.length === 0
  const isAppliedEmpty = appliedIds.length === 0
  const hasChanged =
    localIds.length !== appliedIds.length ||
    localIds.some((id) => !appliedIds.includes(id))

  const clearFilter = () => {
    setColumnFilters((prev) =>
      setColumnFilterValue(prev, COLUMN_IDS.CHAIN_ID, ''),
    )
  }

  const activeLabel = useMemo(() => {
    if (appliedIds.length === 0) return ''
    if (appliedIds.length === 1)
      return (
        <NetworkDisplay layout="inline-text" chainId={Number(appliedIds[0])} />
      )
    return `Networks (${appliedIds.length})`
  }, [appliedIds])

  return (
    <FilterPopoverButton
      label="Network"
      activeLabel={activeLabel}
      isAppliedEmpty={isAppliedEmpty}
      isLocalEmpty={isLocalEmpty}
      hasChanged={hasChanged}
      onApply={() => {
        setColumnFilters((prev) =>
          setColumnFilterValue(
            prev,
            COLUMN_IDS.CHAIN_ID,
            localIds.length > 0 ? JSON.stringify(localIds) : '',
          ),
        )
        // Apply/Reset close without going through onClose — clear here too.
        setSearch('')
      }}
      onReset={() => {
        setLocalIds([])
        setSearch('')
        clearFilter()
      }}
      onClear={() => {
        setLocalIds([])
        setSearch('')
        clearFilter()
      }}
      onClose={() => {
        setLocalIds(appliedIds)
        setSearch('')
      }}
    >
      <div className="flex flex-col gap-3">
        <Input
          inputSize="small"
          placeholder="Search by network..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchIcon className="h-3.5 w-3.5 text-secondary" />}
          onClearInput={search ? () => setSearch('') : undefined}
        />
        {query && visibleChainIds.length === 0 ? (
          // Keep the label in place so the popover doesn't jump.
          <Group label="Network">
            <EmptyState className="py-4 text-center text-sm">
              No networks found
            </EmptyState>
          </Group>
        ) : (
          <FilterScrollFade>
            <CheckboxGroupInput
              label="Network"
              size="small"
              value={localIds}
              onChange={setLocalIds}
              options={visibleChainIds.map((chainId) => ({
                value: chainId.toString(),
                display: () => (
                  <NetworkDisplay
                    layout="standard"
                    size="small"
                    chainId={chainId}
                    highlightText={search}
                  />
                ),
              }))}
            />
          </FilterScrollFade>
        )}
      </div>
    </FilterPopoverButton>
  )
}
